import './styles.css';

import {
  downloadAutoinstall,
  generateAutoinstall,
  redactAutoinstall,
} from './autoinstall';
import { deviceProfiles, findDeviceProfile } from './profiles';
import {
  formIsValid,
  readinessCount,
  validateProvisionForm,
  type ProvisionErrors,
  type ProvisionField,
  type ProvisionFormValues,
} from './validation';

const app = document.querySelector<HTMLDivElement>('#app');

if (!app) {
  throw new Error('Elemento #app ausente');
}

const defaultProfile = deviceProfiles[0];

if (!defaultProfile) {
  throw new Error('Nenhum perfil de dispositivo configurado');
}

app.innerHTML = `
  <main class="workspace">
    <header class="masthead">
      <div class="brand-lockup" aria-label="Shud Provision">
        <span class="brand-mark" aria-hidden="true">S</span>
        <span class="brand-copy">
          <strong>Shud Provision</strong>
          <small>Ubuntu 26.04 LTS</small>
        </span>
      </div>
      <div class="privacy-stamp">
        <span class="privacy-light" aria-hidden="true"></span>
        Processamento local · nenhum dado é enviado
      </div>
    </header>

    <section class="intro" aria-labelledby="page-title">
      <p class="eyebrow">Manifesto de instalação</p>
      <h1 id="page-title">Prepare o Laptop 1<br />para uma instalação limpa.</h1>
      <p class="intro-copy">
        Preencha uma vez, confira o alvo e baixe o arquivo que o instalador do
        Ubuntu lê diretamente da pasta Downloads.
      </p>
    </section>

    <div class="workbench">
      <aside class="machine-passport" aria-label="Dispositivo selecionado">
        <div class="passport-topline">
          <span>Hardware confirmado</span>
          <span class="verified-chip">VERIFICADO</span>
        </div>

        <div class="device-glyph" aria-hidden="true">
          <span class="device-screen">01</span>
          <span class="device-base"></span>
        </div>

        <div class="passport-heading">
          <p class="passport-kicker">Ativo Shud</p>
          <h2 data-profile-label>${defaultProfile.label}</h2>
          <p data-profile-model>${defaultProfile.model}</p>
        </div>

        <dl class="spec-list">
          <div>
            <dt>Memória</dt>
            <dd data-memory>${defaultProfile.memory}</dd>
          </div>
          <div>
            <dt>Destino</dt>
            <dd data-disk-label>${defaultProfile.disk.label}</dd>
          </div>
          <div class="serial-row">
            <dt>Regra de disco</dt>
            <dd data-disk-match>${defaultProfile.disk.match.key}: ${defaultProfile.disk.match.value}</dd>
          </div>
        </dl>

        <div class="target-rule">
          <span class="target-rule-icon" aria-hidden="true">!</span>
          <p>
            O perfil só aceita este alvo. Se ele não existir, a instalação falha;
            não há fallback por tamanho ou posição.
          </p>
        </div>
      </aside>

      <section class="form-panel">
        <div class="readiness-rail" aria-live="polite">
          <div class="readiness-copy">
            <span>Prontidão do manifesto</span>
            <strong><span data-readiness-count>0</span>/5</strong>
          </div>
          <div class="readiness-track" aria-hidden="true">
            <span data-readiness-bar></span>
          </div>
          <span class="readiness-state" data-readiness-state>Preencha os campos essenciais</span>
        </div>

        <form id="provision-form" autocomplete="off" novalidate>
          <section class="form-section" aria-labelledby="section-device">
            <div class="section-index">01</div>
            <div class="section-body">
              <div class="section-heading">
                <div>
                  <h2 id="section-device">Dispositivo</h2>
                  <p>O perfil fixa o hostname, interfaces e disco permitido.</p>
                </div>
              </div>

              <label class="field field-wide">
                <span>Perfil da estação</span>
                <select name="profileId" id="profileId">
                  ${deviceProfiles
                    .map(
                      (profile) =>
                        `<option value="${profile.id}">${profile.label} · ${profile.model}</option>`,
                    )
                    .join('')}
                </select>
                <small>Hostname: <code data-hostname>${defaultProfile.hostname}</code></small>
              </label>
            </div>
          </section>

          <section class="form-section" aria-labelledby="section-identity">
            <div class="section-index">02</div>
            <div class="section-body">
              <div class="section-heading">
                <div>
                  <h2 id="section-identity">Identidade</h2>
                  <p>A senha é convertida em SHA-512 crypt dentro deste navegador.</p>
                </div>
              </div>

              <div class="field-grid">
                <label class="field">
                  <span>Nome exibido</span>
                  <input name="realName" id="realName" value="Shud" autocomplete="off" />
                  <small class="field-error" data-error-for="realName"></small>
                </label>

                <label class="field">
                  <span>Usuário</span>
                  <input
                    name="username"
                    id="username"
                    value="shud"
                    autocapitalize="none"
                    autocomplete="off"
                    spellcheck="false"
                  />
                  <small class="field-error" data-error-for="username"></small>
                </label>

                <label class="field">
                  <span>Senha de login</span>
                  <input
                    type="password"
                    name="loginPassword"
                    id="loginPassword"
                    autocomplete="new-password"
                  />
                  <small class="field-error" data-error-for="loginPassword"></small>
                </label>

                <label class="field">
                  <span>Confirmar senha</span>
                  <input
                    type="password"
                    name="loginPasswordConfirmation"
                    id="loginPasswordConfirmation"
                    autocomplete="new-password"
                  />
                  <small class="field-error" data-error-for="loginPasswordConfirmation"></small>
                </label>
              </div>
            </div>
          </section>

          <section class="form-section" aria-labelledby="section-network">
            <div class="section-index">03</div>
            <div class="section-body">
              <div class="section-heading">
                <div>
                  <h2 id="section-network">Rede e acesso</h2>
                  <p>O Wi‑Fi fica no YAML local; o SSH aceita somente a chave indicada.</p>
                </div>
                <span class="interface-chip" data-interface>${defaultProfile.wifi.interfaceName}</span>
              </div>

              <div class="field-grid">
                <label class="field">
                  <span>Nome do Wi‑Fi (SSID)</span>
                  <input name="wifiSsid" id="wifiSsid" autocomplete="off" spellcheck="false" />
                  <small class="field-error" data-error-for="wifiSsid"></small>
                </label>

                <label class="field">
                  <span>Senha do Wi‑Fi</span>
                  <input
                    type="password"
                    name="wifiPassword"
                    id="wifiPassword"
                    autocomplete="new-password"
                  />
                  <small class="field-error" data-error-for="wifiPassword"></small>
                </label>

                <label class="field field-wide">
                  <span>Chave pública SSH</span>
                  <textarea
                    name="sshPublicKey"
                    id="sshPublicKey"
                    rows="3"
                    autocapitalize="none"
                    autocomplete="off"
                    spellcheck="false"
                  >${defaultProfile.sshPublicKey}</textarea>
                  <small class="field-error" data-error-for="sshPublicKey"></small>
                </label>
              </div>
            </div>
          </section>

          <section class="form-section danger-section" aria-labelledby="section-storage">
            <div class="section-index">04</div>
            <div class="section-body">
              <div class="section-heading">
                <div>
                  <h2 id="section-storage">Disco e criptografia</h2>
                  <p>LVM ocupa todo o NVMe; a criptografia vem ligada por padrão.</p>
                </div>
                <span class="erase-chip">APAGA TUDO</span>
              </div>

              <div class="destructive-target">
                <span>Alvo destrutivo</span>
                <strong data-target-summary>${defaultProfile.disk.label} · ${defaultProfile.disk.size}</strong>
                <code data-target-match>${defaultProfile.disk.match.key}: ${defaultProfile.disk.match.value}</code>
              </div>

              <label class="check-row encryption-check">
                <input type="checkbox" name="encryptDisk" id="encryptDisk" checked />
                <span>
                  <strong>Criptografar o disco com LUKS</strong>
                  <small>Recomendado para laptop. A senha será solicitada ao ligar.</small>
                </span>
              </label>

              <div class="encryption-options" data-encryption-options>
                <label class="check-row reuse-password-check">
                  <input
                    type="checkbox"
                    name="useLoginPasswordForLuks"
                    id="useLoginPasswordForLuks"
                    checked
                  />
                  <span>
                    <strong>Usar a mesma senha de login para o disco</strong>
                    <small>Você digita a senha do usuário somente duas vezes.</small>
                  </span>
                </label>

                <div class="field-grid" data-luks-fields hidden>
                  <label class="field">
                    <span>Passphrase LUKS diferente</span>
                    <input
                      type="password"
                      name="luksPassword"
                      id="luksPassword"
                      autocomplete="new-password"
                    />
                    <small class="field-error" data-error-for="luksPassword"></small>
                  </label>

                  <label class="field">
                    <span>Confirmar passphrase</span>
                    <input
                      type="password"
                      name="luksPasswordConfirmation"
                      id="luksPasswordConfirmation"
                      autocomplete="new-password"
                    />
                    <small class="field-error" data-error-for="luksPasswordConfirmation"></small>
                  </label>
                </div>
              </div>

              <label class="check-row">
                <input type="checkbox" name="installCodecs" id="installCodecs" checked />
                <span>
                  <strong>Instalar codecs e drivers recomendados</strong>
                  <small>O perfil OEM permanece em modo automático.</small>
                </span>
              </label>

              <label class="check-row destructive-check">
                <input type="checkbox" name="confirmErase" id="confirmErase" />
                <span>
                  <strong>Autorizo apagar integralmente o NVMe interno acima</strong>
                  <small>Pendrive e discos USB não fazem parte desta autorização.</small>
                  <small class="field-error" data-error-for="confirmErase"></small>
                </span>
              </label>
            </div>
          </section>

          <section class="form-section output-section" aria-labelledby="section-output">
            <div class="section-index">05</div>
            <div class="section-body">
              <div class="section-heading">
                <div>
                  <h2 id="section-output">Arquivo local</h2>
                  <p>Prepare, revise e baixe. Fechar ou atualizar a página apaga o estado.</p>
                </div>
                <label class="reveal-control">
                  <input type="checkbox" id="revealSecrets" disabled />
                  Mostrar segredos
                </label>
              </div>

              <div class="yaml-preview" data-preview-state="empty">
                <div class="preview-toolbar">
                  <span>autoinstall.yaml</span>
                  <span data-preview-label>Aguardando formulário</span>
                </div>
                <pre id="yamlPreview" tabindex="0"># O YAML será exibido aqui após a validação.</pre>
              </div>

              <div class="action-row">
                <button type="submit" class="prepare-button">Preparar arquivo</button>
                <button type="button" class="download-button" id="downloadButton" disabled>
                  Baixar autoinstall.yaml
                </button>
              </div>

              <p class="action-note" id="actionNote" aria-live="polite">
                O download só é liberado após as cinco verificações.
              </p>
            </div>
          </section>
        </form>
      </section>
    </div>

    <footer class="page-footer">
      <span>SHUD / PROVISIONAMENTO LOCAL</span>
      <span>SEM BACKEND · SEM TELEMETRIA · SEM PERSISTÊNCIA</span>
    </footer>
  </main>
`;

const form = requiredElement<HTMLFormElement>('#provision-form');
const readinessCountElement = requiredElement<HTMLElement>('[data-readiness-count]');
const readinessBar = requiredElement<HTMLElement>('[data-readiness-bar]');
const readinessState = requiredElement<HTMLElement>('[data-readiness-state]');
const yamlPreview = requiredElement<HTMLElement>('#yamlPreview');
const previewContainer = requiredElement<HTMLElement>('.yaml-preview');
const previewLabel = requiredElement<HTMLElement>('[data-preview-label]');
const revealSecrets = requiredElement<HTMLInputElement>('#revealSecrets');
const downloadButton = requiredElement<HTMLButtonElement>('#downloadButton');
const actionNote = requiredElement<HTMLElement>('#actionNote');

let preparedYaml: string | null = null;

function requiredElement<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);

  if (!element) {
    throw new Error(`Elemento obrigatório ausente: ${selector}`);
  }

  return element;
}

function inputValue(name: ProvisionField): string {
  return requiredElement<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
    `[name="${name}"]`,
  ).value;
}

function collectValues(): ProvisionFormValues {
  return {
    profileId: inputValue('profileId'),
    realName: inputValue('realName'),
    username: inputValue('username'),
    loginPassword: inputValue('loginPassword'),
    loginPasswordConfirmation: inputValue('loginPasswordConfirmation'),
    wifiSsid: inputValue('wifiSsid'),
    wifiPassword: inputValue('wifiPassword'),
    encryptDisk: requiredElement<HTMLInputElement>('#encryptDisk').checked,
    useLoginPasswordForLuks: requiredElement<HTMLInputElement>(
      '#useLoginPasswordForLuks',
    ).checked,
    luksPassword: inputValue('luksPassword'),
    luksPasswordConfirmation: inputValue('luksPasswordConfirmation'),
    sshPublicKey: inputValue('sshPublicKey'),
    installCodecs: requiredElement<HTMLInputElement>('#installCodecs').checked,
    confirmErase: requiredElement<HTMLInputElement>('#confirmErase').checked,
  };
}

function renderErrors(errors: ProvisionErrors): void {
  document.querySelectorAll<HTMLElement>('[data-error-for]').forEach((element) => {
    const field = element.dataset.errorFor as ProvisionField;
    element.textContent = errors[field] ?? '';
  });

  form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('[name]').forEach((field) => {
    const fieldName = field.name as ProvisionField;
    const hasError = Boolean(errors[fieldName]);
    field.toggleAttribute('aria-invalid', hasError);
  });
}

function renderReadiness(): void {
  const values = collectValues();
  const count = readinessCount(values);
  const percentage = `${count * 20}%`;

  readinessCountElement.textContent = String(count);
  readinessBar.style.width = percentage;
  readinessState.textContent = count === 5 ? 'Pronto para validar' : 'Preencha os campos essenciais';
  readinessState.dataset.ready = count === 5 ? 'true' : 'false';
}

function renderProfile(profileId: string): void {
  const profile = findDeviceProfile(profileId);
  const diskMatch = `${profile.disk.match.key}: ${profile.disk.match.value}`;
  const textBindings: Array<[string, string]> = [
    ['[data-profile-label]', profile.label],
    ['[data-profile-model]', profile.model],
    ['[data-memory]', profile.memory],
    ['[data-disk-label]', profile.disk.label],
    ['[data-disk-match]', diskMatch],
    ['[data-hostname]', profile.hostname],
    ['[data-interface]', profile.wifi.interfaceName],
    ['[data-target-summary]', `${profile.disk.label} · ${profile.disk.size}`],
    ['[data-target-match]', diskMatch],
  ];

  textBindings.forEach(([selector, value]) => {
    requiredElement<HTMLElement>(selector).textContent = value;
  });

  requiredElement<HTMLTextAreaElement>('#sshPublicKey').value = profile.sshPublicKey;
}

function renderEncryptionControls(): void {
  const encryptDisk = requiredElement<HTMLInputElement>('#encryptDisk');
  const useLoginPassword = requiredElement<HTMLInputElement>('#useLoginPasswordForLuks');
  const encryptionOptions = requiredElement<HTMLElement>('[data-encryption-options]');
  const luksFields = requiredElement<HTMLElement>('[data-luks-fields]');
  const showSeparatePassword = encryptDisk.checked && !useLoginPassword.checked;

  useLoginPassword.disabled = !encryptDisk.checked;
  encryptionOptions.toggleAttribute('hidden', !encryptDisk.checked);
  luksFields.toggleAttribute('hidden', !showSeparatePassword);
  luksFields.querySelectorAll<HTMLInputElement>('input').forEach((input) => {
    input.disabled = !showSeparatePassword;
  });
}

function renderPreview(): void {
  if (!preparedYaml) {
    yamlPreview.textContent = '# O YAML será exibido aqui após a validação.';
    return;
  }

  yamlPreview.textContent = revealSecrets.checked
    ? preparedYaml
    : redactAutoinstall(preparedYaml);
}

function invalidatePreparedFile(): void {
  if (!preparedYaml) return;

  preparedYaml = null;
  downloadButton.disabled = true;
  revealSecrets.checked = false;
  revealSecrets.disabled = true;
  previewContainer.dataset.previewState = 'empty';
  previewLabel.textContent = 'Alterações não preparadas';
  actionNote.textContent = 'Prepare o arquivo novamente para incorporar as alterações.';
  renderPreview();
}

form.addEventListener('input', (event) => {
  if (event.target === revealSecrets) {
    return;
  }

  renderEncryptionControls();
  invalidatePreparedFile();
  renderReadiness();
});

requiredElement<HTMLSelectElement>('#profileId').addEventListener('change', (event) => {
  renderProfile((event.currentTarget as HTMLSelectElement).value);
  invalidatePreparedFile();
});

revealSecrets.addEventListener('change', renderPreview);

form.addEventListener('submit', (event) => {
  event.preventDefault();

  const values = collectValues();
  const errors = validateProvisionForm(values);
  renderErrors(errors);

  if (!formIsValid(errors)) {
    preparedYaml = null;
    downloadButton.disabled = true;
    previewContainer.dataset.previewState = 'error';
    previewLabel.textContent = 'Campos pendentes';
    actionNote.textContent = 'Revise os campos destacados antes de preparar o arquivo.';

    const firstInvalid = form.querySelector<HTMLElement>('[aria-invalid="true"]');
    firstInvalid?.focus();
    return;
  }

  const profile = findDeviceProfile(values.profileId);
  preparedYaml = generateAutoinstall(values, profile);
  revealSecrets.disabled = false;
  revealSecrets.checked = false;
  downloadButton.disabled = false;
  previewContainer.dataset.previewState = 'ready';
  previewLabel.textContent = 'Validado localmente';
  actionNote.textContent = 'Confira o alvo e baixe o arquivo para a pasta Downloads.';
  renderPreview();
});

downloadButton.addEventListener('click', () => {
  if (!preparedYaml) return;

  downloadAutoinstall(preparedYaml);
  actionNote.textContent =
    'Arquivo baixado. No instalador, escolha Instalação automatizada → Selecionar arquivo.';
});

renderProfile(defaultProfile.id);
renderEncryptionControls();
renderReadiness();
